import React, { useEffect, useState } from 'react';
import { Download, Trash2, Clock, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { listConversionTasks, deleteConversionTask, getDownloadUrl } from '../../services/conversionApi';
import { ConversionTask } from '../../types/conversion.types';
import Button from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import './ConversionHistory.css';

interface Props {
    onRefreshTrigger?: number;
    operationFilter?: string | string[]; // e.g. 'merge', 'split', or ['pdf_to_word', 'word_to_pdf']
}

const OPERATION_NAME_MAP: Record<string, string> = {
    merge: '合并 PDF',
    split: '拆分 PDF',
    compress: '压缩 PDF',
    delete_pages: '删除页面',
    pdf_to_word: 'PDF 转 Word',
    word_to_pdf: 'Word 转 PDF',
    pdf_to_excel: 'PDF 转 Excel',
    excel_to_pdf: 'Excel 转 PDF',
    pdf_to_ppt: 'PDF 转 PPT',
    ppt_to_pdf: 'PPT 转 PDF',
    pdf_to_image: 'PDF 转 图片',
    image_to_pdf: '图片 转 PDF',
    pdf_to_html: 'PDF 转 HTML',
    html_to_pdf: 'HTML 转 PDF',
};

const ConversionHistory: React.FC<Props> = ({ onRefreshTrigger = 0, operationFilter }) => {
    const [tasks, setTasks] = useState<ConversionTask[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 10;

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

    const fetchTasks = async () => {
        setIsLoading(true);
        try {
            const operationString = Array.isArray(operationFilter) ? operationFilter.join(',') : operationFilter;
            const resp = await listConversionTasks({ page, pageSize, operation: operationString });
            setTasks(resp.items);
            setTotal(resp.total);
        } catch (error) {
            console.error('Failed to fetch conversion history:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, onRefreshTrigger, operationFilter]);

    const confirmDelete = (taskId: string) => {
        setTaskToDelete(taskId);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!taskToDelete) return;

        try {
            await deleteConversionTask(taskToDelete);
            setIsDeleteModalOpen(false);
            setTaskToDelete(null);

            if (tasks.length === 1 && page > 1) {
                setPage(p => p - 1);
            } else {
                fetchTasks();
            }
        } catch (error) {
            console.error('Failed to delete task:', error);
            alert('删除失败，请稍后重试');
            setIsDeleteModalOpen(false);
            setTaskToDelete(null);
        }
    };

    const cancelDelete = () => {
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
    };

    const handleDownload = (taskId: string) => {
        const url = getDownloadUrl(taskId);
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString();
    };

    const formatSize = (bytes?: number) => {
        if (!bytes) return '-';
        return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    };

    if (tasks.length === 0 && !isLoading) {
        return (
            <div className="conversion-history empty">
                <p>暂无操作记录</p>
            </div>
        );
    }

    return (
        <div className="conversion-history">
            <div className="history-header">
                <h3>{operationFilter ? (typeof operationFilter === 'string' ? `${OPERATION_NAME_MAP[operationFilter] || operationFilter} 历史记录` : '格式转换历史记录') : '最近的 PDF 操作记录'}</h3>
                <button className="refresh-btn" onClick={fetchTasks} disabled={isLoading}>
                    <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
                </button>
            </div>

            <div className="table-responsive">
                <table className="history-table">
                    <thead>
                        <tr>
                            <th>文件名</th>
                            <th>操作类型</th>
                            <th>状态</th>
                            <th>生成大小</th>
                            <th>时间</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map(task => (
                            <tr key={task.task_id}>
                                <td className="filename-cell" title={task.source_filename}>
                                    {task.source_filename}
                                </td>
                                <td>{OPERATION_NAME_MAP[task.operation] || task.operation}</td>
                                <td>
                                    <span className={`status-badge status-${task.status}`}>
                                        {task.status === 'completed' && <CheckCircle2 size={14} />}
                                        {task.status === 'failed' && <AlertCircle size={14} />}
                                        {(task.status === 'pending' || task.status === 'processing') && <Clock size={14} />}
                                        <span>
                                            {task.status === 'completed' ? '已完成' :
                                                task.status === 'failed' ? '失败' :
                                                    task.status === 'processing' ? '处理中' :
                                                        task.status === 'pending' ? '等待中' : task.status}
                                        </span>
                                    </span>
                                </td>
                                <td className="size-cell">{formatSize(task.file_size_out)}</td>
                                <td className="date-cell">{formatDate(task.created_at)}</td>
                                <td className="actions-cell">
                                    {task.status === 'completed' && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleDownload(task.task_id)}
                                            title="下载结果文件"
                                        >
                                            <Download size={14} />
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="delete-btn"
                                        onClick={() => confirmDelete(task.task_id)}
                                        title="删除记录"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {total > pageSize && (
                <div className="pagination">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        上一页
                    </Button>
                    <span className="page-info">{page} / {Math.ceil(total / pageSize)}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= Math.ceil(total / pageSize)}
                        onClick={() => setPage(p => p + 1)}
                    >
                        下一页
                    </Button>
                </div>
            )}

            <ConfirmModal
                isOpen={isDeleteModalOpen}
                title="删除记录"
                message="确定要删除这条记录吗？此操作也会同时删除相关的源文件和结果文件，且不可恢复。"
                confirmText="确定"
                cancelText="取消"
                onConfirm={handleDelete}
                onCancel={cancelDelete}
                isDestructive={true}
            />
        </div>
    );
};

export default ConversionHistory;
